/* assets/mos-dropdown.js — header search dropdown, empty-query state only.
 *
 * The dropdown is built in JS by assets/predictive-search.js
 * (buildSsDropdownResultsMarkup) and injected after load, so none of this can be done in
 * Liquid. That file also cannot be edited: assets/header-search-dropdown.js is 792KB, over
 * the theme file write limit. So this runs after each render and adjusts the result.
 *
 * Four changes, all confined to the state with an empty search box:
 *
 *   1. Popular Products goes from one row of 5 to two rows of 5. Searchspring already
 *      returns 12 results for the empty query (autocomplete.json, resultsPerPage=12), so
 *      there is no extra request — the cards are already in the DOM, sitting in a Swiper
 *      that only shows five of them.
 *   2. A "Just bought" list in the left column, from a JSON feed of recent orders.
 *   3. Category shortcut chips along the bottom, from a Shopify menu.
 *   4. The Popular Searches list is trimmed, because 12 pills set the height of the whole
 *      dropdown: measured at 1440x1000, the panel was 680px tall while the product row used
 *      about 400px of it, leaving ~250px of empty white under the products. Trimming the
 *      pills and using that space is what pays for the second product row.
 *
 * The typed-query state keeps only change 1, the two-row grid, because Searchspring returns
 * 12 matches for a query as well and the theme's single row shows five of them. Just bought
 * and the category chips are empty-state content and come off as soon as something is typed.
 */
(function () {
  'use strict';

  if (window.__mosDropdown) return;

  var CFG = window.mosDropdownConfig || {};
  var API = (window.__mosDropdown = {
    version: 1,
    renders: 0,
    gridApplied: 0,
    gridOnResults: 0,
    soldRendered: 0,
    chipsRendered: 0,
    pillsTrimmed: 0,
    feed: null,
    feedState: 'idle',
    lastError: null
  });

  var PER_ROW = num(CFG.perRow, 5);
  var ROWS = num(CFG.rows, 2);
  var MAX_CARDS = PER_ROW * ROWS;
  var PILLS = num(CFG.maxPills, 6);
  var PILLS_MB = num(CFG.maxPillsMobile, 4);
  var SOLD_MAX = num(CFG.soldMax, 4);
  var GRID_MIN = num(CFG.gridMinWidth, 990);
  var RESULTS_ROWS = CFG.resultsRows !== false;

  function wide() { return window.innerWidth >= GRID_MIN; }

  function num(v, d) {
    v = parseInt(v, 10);
    return isFinite(v) && v > 0 ? v : d;
  }

  /* ---------------------------------------------------------------- feed */

  function loadFeed() {
    if (API.feedState !== 'idle') return;
    if (!CFG.showSold || !CFG.soldUrl) { API.feedState = 'off'; return; }
    API.feedState = 'loading';
    fetch(CFG.soldUrl, { credentials: 'omit' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (d) {
        var list = d && Array.isArray(d.products) ? d.products : null;
        if (!list || !list.length) { API.feedState = 'empty'; return; }
        // The generator already excludes quote-only handles, but it runs on a schedule and
        // the quote-only list can change in between, so the live verdict wins when it is
        // available. A quote-only product has no price to show, which is the whole point of
        // this row, so it is dropped rather than shown priceless.
        var qo = window.__mosQuoteOnly;
        API.feed = list.filter(function (p) {
          if (!p || !p.handle || !p.image || !(p.price > 0)) return false;
          if (qo && qo.ready && !qo.failed && qo.isQuoteOnly(p.handle)) return false;
          return true;
        }).slice(0, SOLD_MAX);
        API.feedState = API.feed.length ? 'ready' : 'empty';
        apply();
      })
      .catch(function (e) { API.feedState = 'error'; API.lastError = String(e); });
  }

  function money(dollars) {
    var cents = Math.round(Number(dollars) * 100);
    // Prices in the feed are ex-GST, the store convention. The theme adds GST for display
    // and formats the currency; using its own function is the only way this row agrees with
    // every other price on the page.
    try {
      if (window.theme && typeof window.theme.formatDisplayMoney === 'function') {
        return window.theme.formatDisplayMoney(cents, [], true);
      }
    } catch (e) {}
    return '$' + (cents / 100).toFixed(2);
  }

  /* ------------------------------------------------------------- helpers */

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // The dropdown shows Popular Products when the box is empty and real matches once
  // something is typed. The two states share the grid but not the added content.
  function isInitialState(root) {
    var inp = root.querySelector('input[type="search"], input[name="q"]');
    if (inp && String(inp.value || '').trim().length) return false;
    return true;
  }

  /* -------------------------------------------------------- 1. two rows */

  function applyGrid(root) {
    var slider = root.querySelector('.predictive-search-products-slider');
    if (!slider) return;
    var wrap = slider.querySelector('.swiper-wrapper');
    if (!wrap) return;

    // Narrow screens keep the theme's swipeable row. Two rows of five becomes five rows of
    // two on a phone, which measured 10 cards deep and pushed the products to y=836 —
    // completely below the fold. The category chips are the useful part on mobile.
    if (!wide()) {
      slider.removeAttribute('data-mos-dd-grid');
      wrap.removeAttribute('data-mos-dd-grid');
      var all = wrap.querySelectorAll('[data-mos-dd-hide]');
      for (var k = 0; k < all.length; k++) all[k].removeAttribute('data-mos-dd-hide');
      return;
    }

    slider.setAttribute('data-mos-dd-grid', ROWS);
    wrap.setAttribute('data-mos-dd-grid', ROWS);

    var slides = wrap.querySelectorAll('.swiper-slide');
    for (var i = 0; i < slides.length; i++) {
      // Swiper writes margin-right inline for spaceBetween. In a grid that lands on top of
      // the grid gap and pushes the last column out of the row, so it is cleared here
      // rather than fought with !important.
      slides[i].style.marginRight = '0px';
      slides[i].style.width = '';
      if (i >= MAX_CARDS) slides[i].setAttribute('data-mos-dd-hide', '1');
      else slides[i].removeAttribute('data-mos-dd-hide');
    }

    // Swiper writes its position as an inline transform on the wrapper. The stylesheet pins
    // it with transform:none !important, which an inline style cannot beat, so the Swiper
    // instance is left completely alone — no destroy, no event hooks. That matters because
    // the theme re-creates the instance on every render, and fighting it would never end.
    API.gridApplied++;
  }

  /* ------------------------------------------------------- 4. trim pills */

  function trimPills(root) {
    var side = root.querySelector('.predictive-search__sidebar');
    if (!side) return;
    var sec = side.querySelector('.predictive-search__sidebar-section');
    if (!sec) return;
    var keep = wide() ? PILLS : PILLS_MB;
    var items = sec.querySelectorAll('li');
    for (var i = 0; i < items.length; i++) {
      if (i >= keep) items[i].setAttribute('data-mos-dd-hide', '1');
      else items[i].removeAttribute('data-mos-dd-hide');
    }
    API.pillsTrimmed++;
  }

  /* -------------------------------------------------- 2. just bought row */

  function renderSold(root) {
    if (API.feedState !== 'ready' || !API.feed || !API.feed.length) return;
    var side = root.querySelector('.predictive-search__sidebar');
    if (!side || side.querySelector('[data-mos-dd-sold]')) return;

    var html = '<div class="predictive-search__sidebar-section mos-dd-sold" data-mos-dd-sold="1">' +
      '<h3 class="predictive-search__sidebar-heading">' + esc(CFG.soldHeading || 'Just bought') + '</h3>' +
      '<ul class="mos-dd-sold__list list-unstyled">';
    for (var i = 0; i < API.feed.length; i++) {
      var p = API.feed[i];
      html += '<li class="mos-dd-sold__item"><a class="mos-dd-sold__link" href="/products/' +
        esc(p.handle) + '">' +
        '<span class="mos-dd-sold__thumb"><img src="' + esc(p.image) +
        '" alt="" loading="lazy" width="44" height="44"></span>' +
        '<span class="mos-dd-sold__text">' +
        '<span class="mos-dd-sold__title">' + esc(p.title) + '</span>' +
        '<span class="mos-dd-sold__price">' + esc(money(p.price)) + '</span>' +
        '</span></a></li>';
    }
    html += '</ul></div>';

    var d = document.createElement('div');
    d.innerHTML = html;
    side.appendChild(d.firstChild);
    API.soldRendered++;
  }

  /* ----------------------------------------------------- 3. category chips */

  function renderChips(root) {
    var chips = CFG.chips;
    if (!CFG.showChips || !chips || !chips.length) return;
    var group = root.querySelector('.predictive-search__result-group');
    if (!group || group.querySelector('[data-mos-dd-chips]')) return;

    var html = '<div class="mos-dd-chips" data-mos-dd-chips="1">' +
      '<span class="mos-dd-chips__heading">' + esc(CFG.chipsHeading || 'Browse by category') + '</span>' +
      '<ul class="mos-dd-chips__list list-unstyled">';
    for (var i = 0; i < chips.length; i++) {
      var c = chips[i];
      if (!c || !c.url || !c.label) continue;
      html += '<li class="mos-dd-chips__item"><a class="mos-dd-chips__link" href="' +
        esc(c.url) + '">' + esc(c.label) + '</a></li>';
    }
    html += '</ul></div>';

    var d = document.createElement('div');
    d.innerHTML = html;
    // Prepended, not appended. Measured at 1440x900 with the chips at the bottom: the row
    // landed at y=891..958 while the viewport ended at 900, so it sat below the fold and the
    // dropdown looked like it ended at the products. Above the grid it is the first thing in
    // the column and the two product rows still finish at y=798.
    group.insertBefore(d.firstChild, group.firstChild);
    API.chipsRendered++;
  }

  /* ---------------------------------------------------------------- apply */

  function apply() {
    var hosts = document.querySelectorAll('predictive-search');
    for (var i = 0; i < hosts.length; i++) {
      var root = hosts[i];
      var wrap = root.querySelector('.predictive-search__results-groups-wrapper');
      if (!wrap) continue;

      if (!isInitialState(root)) {
        // A query has been typed. Just bought and the category chips are empty-state content
        // and come off. The grid stays, because Searchspring returns 12 matches here too and
        // the theme's single row shows five of them — the other seven are already in the DOM.
        clearExtras(root);
        try {
          if (CFG.twoRows && RESULTS_ROWS) { applyGrid(root); API.gridOnResults++; }
          else clearGrid(root);
        } catch (e) { API.lastError = String(e); }
        continue;
      }
      try { if (CFG.twoRows) applyGrid(root); } catch (e) { API.lastError = String(e); }
      try { trimPills(root); } catch (e) { API.lastError = String(e); }
      try { renderSold(root); } catch (e) { API.lastError = String(e); }
      try { renderChips(root); } catch (e) { API.lastError = String(e); }
    }
    API.renders++;
  }

  // Just bought and the category chips: added content, removed whenever it does not belong.
  function clearExtras(root) {
    var els = root.querySelectorAll('[data-mos-dd-sold], [data-mos-dd-chips]');
    for (var i = 0; i < els.length; i++) els[i].parentNode.removeChild(els[i]);
  }

  // The grid attributes, taken back off so the theme's own swipeable row returns.
  function clearGrid(root) {
    var i, els;
    els = root.querySelectorAll('[data-mos-dd-hide]');
    for (i = 0; i < els.length; i++) els[i].removeAttribute('data-mos-dd-hide');
    els = root.querySelectorAll('[data-mos-dd-grid]');
    for (i = 0; i < els.length; i++) els[i].removeAttribute('data-mos-dd-grid');
  }

  /* ----------------------------------------------------------------- boot */

  var pending = null;
  function schedule() {
    if (pending) return;
    pending = setTimeout(function () { pending = null; try { apply(); } catch (e) {} }, 60);
  }

  function start() {
    loadFeed();
    var obs = new MutationObserver(schedule);
    obs.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['results', 'open', 'value']
    });
    document.addEventListener('input', schedule, true);
    document.addEventListener('focusin', schedule, true);
    window.addEventListener('resize', schedule);
    schedule();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
